export type ExampleVisual = "portrait" | "landscape" | "recipe" | "comic" | "keepsake";

export interface FinishedBookExample {
  bestFor: string;
  commitment: string;
  chapter: string;
  sourcePrompt: string;
  opening: string;
  quote: string;
  detail: string;
  visual: ExampleVisual;
}

export const finishedBookExamples: Record<string, FinishedBookExample> = {
  story: {
    bestFor: "A whole life, in their voice",
    commitment: "About 18 conversations",
    chapter: "The House on Maple Street",
    sourcePrompt: "Describe the house you grew up in—walk me through the front door.",
    opening:
      "The screen door at 214 Maple Street announced every arrival with a squeak. Mom was always going to fix it, though somehow she never did.",
    quote: "Home began with a yellow table and the radio playing every morning.",
    detail: "Eleanor and June on the front steps, summer 1962",
    visual: "portrait",
  },
  mother: {
    bestFor: "The woman behind ‘Mom’",
    commitment: "About 16 conversations",
    chapter: "Before We Called Her Mom",
    sourcePrompt: "What was your life like right before kids?",
    opening:
      "Before the lunch boxes and school runs, Rose caught the 6:12 train into the city with red lipstick, a sketchbook, and exactly enough change for coffee.",
    quote: "I was somebody before I was Mom—and motherhood made me more of her, not less.",
    detail: "Rose outside the design studio, spring 1974",
    visual: "portrait",
  },
  dad: {
    bestFor: "The stories Dad rarely tells",
    commitment: "About 16 conversations",
    chapter: "The Blue Ford",
    sourcePrompt: "Tell me about your first car.",
    opening:
      "It was a blue ’68 Ford with a passenger door that opened only from the outside. Frank paid four hundred dollars and spent every Saturday making it roadworthy.",
    quote: "That car taught me the difference between getting somewhere and earning the way there.",
    detail: "Frank, the Ford, and one very questionable haircut",
    visual: "landscape",
  },
  love: {
    bestFor: "A marriage or partnership",
    commitment: "One storyteller, 17 prompts",
    chapter: "The Rainy First Date",
    sourcePrompt: "Tell me about your first date, start to finish.",
    opening:
      "They planned a picnic. The sky had other ideas. By the time Mara arrived, Daniel was sheltering under a newspaper with two sandwiches and a brave little bunch of daisies.",
    quote: "The picnic washed away. The conversation lasted six hours.",
    detail: "Mara and Daniel, forty years after the rain",
    visual: "keepsake",
  },
  children: {
    bestFor: "True family stories at bedtime",
    // Live guided books took 21 to 31 minutes (2026-10-14).
    commitment: "About half an hour",
    chapter: "The Night the Stars Came Down",
    sourcePrompt: "What’s the most magical thing you saw growing up?",
    opening:
      "When Grandpa Leo was eight, the whole field began to blink. Not with stars above him—but with thousands of fireflies floating through the tall summer grass.",
    quote: "For one bright minute, the sky was everywhere.",
    detail: "A true memory, illustrated for little listeners",
    visual: "landscape",
  },
  family: {
    bestFor: "Many voices, one family record",
    commitment: "About 18 shared prompts",
    chapter: "Sunday at the Long Table",
    sourcePrompt: "What was Sunday dinner like when you were growing up?",
    opening:
      "By noon, every chair had been borrowed from somewhere else. The little ones sat on phone books while Aunt Nell carried in a bowl no one was allowed to touch yet.",
    quote: "There was always room, even when there wasn’t a chair.",
    detail: "The Alvarez family table, assembled from five households",
    visual: "keepsake",
  },
  ancestry: {
    bestFor: "Origins, migrations, and heritage",
    commitment: "About 14 deep conversations",
    chapter: "What They Carried",
    sourcePrompt: "What did they leave behind, and what did they carry?",
    opening:
      "Ana packed one wool coat, a brass key, and the recipe her mother wrote in the margins of a church bulletin. The key opened no door in her new country, but she kept it all her life.",
    quote: "Some heirlooms are valuable because they no longer unlock anything.",
    detail: "The key, recipe, and passenger record preserved together",
    visual: "keepsake",
  },
  action: {
    bestFor: "A real life told like an adventure",
    commitment: "About 15 cinematic prompts",
    chapter: "The River Crossing",
    sourcePrompt: "Tell me about a time you were scared and did it anyway.",
    opening:
      "The bridge was gone. Sam could hear the water before he saw it, high and fast after three days of rain. Turning back would leave the whole crew stranded.",
    quote: "Courage wasn’t feeling ready. It was taking the first step while still afraid.",
    detail: "A true event paced as a cinematic chapter",
    visual: "landscape",
  },
  lovestory: {
    bestFor: "One unforgettable romance",
    commitment: "About 15 intimate prompts",
    chapter: "Letters Across the Ocean",
    sourcePrompt: "What kept you connected when you were apart?",
    opening:
      "Every Thursday, a blue envelope crossed the ocean. Lila saved all fifty-two, tied in the same ribbon that later held her wedding bouquet.",
    quote: "We learned to build a life one page at a time.",
    detail: "Original letters reproduced beside the story they carried",
    visual: "keepsake",
  },
  holiday: {
    bestFor: "Traditions everyone remembers differently",
    commitment: "About 14 festive prompts",
    chapter: "The Year of the Crooked Tree",
    sourcePrompt: "Tell me about a holiday that did not go according to plan.",
    opening:
      "The tree leaned left, the oven quit at three, and someone wrapped the television remote. It became the Christmas everyone asked to hear about again.",
    quote: "Perfect holidays disappear. The crooked ones become family history.",
    detail: "Handwritten place cards and the famous leaning tree",
    visual: "keepsake",
  },
  recipes: {
    bestFor: "Recipes with the stories intact",
    commitment: "About 15 dishes and memories",
    chapter: "Sunday Gravy",
    sourcePrompt: "Which recipe carries the most family history?",
    opening:
      "Start the onions before anyone else wakes up. Rosa never measured the garlic; she watched until the bottom of the pot looked right, then added one clove more.",
    quote: "The missing measurement was always: enough for whoever might stop by.",
    detail: "Rosa’s notes, ingredient list, and family variations",
    visual: "recipe",
  },
  comic: {
    bestFor: "Big personalities and visual storytellers",
    commitment: "About 14 scenes",
    chapter: "The Great Pie Rescue",
    sourcePrompt: "What family mishap still makes everyone laugh?",
    opening:
      "One runaway dessert. Three determined cousins. And a downhill driveway that had never looked quite so steep.",
    quote: "Nobody saved the pie. Everybody saved the story.",
    detail: "A true family caper rebuilt as a six-panel scene",
    visual: "comic",
  },
};

export function getFinishedBookExample(templateId: string) {
  return finishedBookExamples[templateId] ?? finishedBookExamples.story!;
}
